import React, { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import BoatCard from '../components/BoatCard'
import SEO from '../components/SEO'

function AnimatedCounter({ target, suffix = '', prefix = '', duration = 1800 }) {
  const [count, setCount] = useState(0)
  const elementRef = useRef(null)
  const [hasAnimated, setHasAnimated] = useState(false)

  useEffect(() => {
    const node = elementRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true)
          let startTimestamp = null
          const step = (timestamp) => {
            if (!startTimestamp) startTimestamp = timestamp
            const progress = Math.min((timestamp - startTimestamp) / duration, 1)
            // Ease out quad formula for smooth decelerating animation
            const easeProgress = 1 - (1 - progress) * (1 - progress)
            setCount(Math.floor(easeProgress * target))
            if (progress < 1) {
              window.requestAnimationFrame(step)
            } else {
              setCount(target)
            }
          }
          window.requestAnimationFrame(step)
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(node)

    return () => {
      observer.disconnect()
    }
  }, [target, duration, hasAnimated])

  return (
    <span ref={elementRef}>
      {prefix}{count}{suffix}
    </span>
  )
}

export default function HomePage() {
  const [featuredBoats, setFeaturedBoats] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Fetch boats dynamically from CMS
    fetch('https://api.dev.auxo.dk/public/v2/content/baade?limit=6')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items) {
          setFeaturedBoats(data.items)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  return (
    <div className="space-y-16 lg:space-y-24">
      <SEO
        title="Nordfyn Marine"
        description="Nordfyn Marine - Bådformidling og salg af sejlbåde, motorbåde og motorsejlere. Professionel rådgivning og Solgt eller gratis."
      />
      {/* 1. HERO SECTION */}
      <section className="relative min-h-[85vh] flex items-center justify-center bg-[#0d3859] text-white overflow-hidden -mt-20 pt-28 pb-20">
        {/* Background Image with Oceanic Blend Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/smuk-luksus-motorbaad-hero.png"
            alt="Nordfyn Marine Bådformidling"
            className="w-full h-full object-cover object-center scale-105 transform transition-transform duration-10000"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d3859] via-[#0d3859]/90 40% to-transparent 75%" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 text-left py-12">
          <div className="max-w-3xl space-y-6 text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#2BABE3]/20 border border-[#2BABE3]/40 text-[#2BABE3] text-[10px] font-semibold tracking-[0.25em] uppercase rounded-full">
              <i className="fa-solid fa-anchor text-[9px]" aria-hidden="true" />
              <span>Professionel Bådformidling i Hele Danmark</span>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-serif font-light text-white tracking-tight leading-tight">
              Tryg og professionel handel af brugte både
            </h1>

            <p className="text-sm sm:text-base text-slate-300 font-light leading-relaxed max-w-2xl">
              Nordfyn Marine hjælper dig med køb og salg af motorbåde, motorsejlere og sejlbåde. Vi sikrer en gennemskuelig proces og rådgiver personligt fra start til slut.
            </p>

            {/* Solgt eller gratis Highlight */}
            <div className="bg-[#1E7BC1]/90 backdrop-blur-md p-5 border border-[#2BABE3]/40 flex items-center gap-4 max-w-lg rounded-2xl">
              <div className="w-10 h-10 bg-[#2BABE3]/20 text-[#2BABE3] flex items-center justify-center shrink-0 border border-[#2BABE3]/40 rounded-xl">
                <i className="fa-solid fa-award text-lg" aria-hidden="true" />
              </div>
              <div>
                <p className="font-serif font-normal text-white text-base tracking-wide uppercase text-xs">
                  Solgt eller gratis
                </p>
                <p className="text-xs text-slate-300 font-light">
                  Bliver båden ikke solgt, er det gratis. Ingen skjulte gebyrer.
                </p>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 pt-3">
              <Link
                to="/baade"
                className="inline-flex items-center gap-2.5 bg-[#2BABE3] hover:bg-[#1E7BC1] text-white border border-[#2BABE3] hover:border-[#1E7BC1] px-7 py-3.5 text-[11px] uppercase tracking-[0.2em] font-semibold transition-all rounded-xl shadow-md"
              >
                <span>Se alle både til salg</span>
                <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
              </Link>

              <Link
                to="/saelg-din-baad"
                className="inline-flex items-center gap-2.5 bg-transparent hover:bg-white hover:text-[#1E7BC1] border border-white text-white px-7 py-3.5 text-[11px] uppercase tracking-[0.2em] font-semibold transition-all rounded-xl"
              >
                <i className="fa-solid fa-tag text-[#2BABE3]" aria-hidden="true" />
                <span>Sælg din båd</span>
              </Link>
            </div>
          </div>
        </div>

        {/* BOTTOM ORGANIC WAVE TRANSITION */}
        <div className="absolute -bottom-1 left-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg
            className="relative block w-full h-[30px] sm:h-[50px] lg:h-[75px] text-white"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            fill="currentColor"
          >
            <path d="M0,40 C300,100 800,50 1200,90 L1200,125 L0,125 Z"></path>
          </svg>
        </div>
      </section>

      {/* 2. KEY METRICS COUNTER BAR (Inspired by Pinterest Design Reference Section 3) */}
      <section className="bg-white py-10 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-slate-100">
            <div className="p-2">
              <span className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light text-slate-900 block">
                <AnimatedCounter target={35} suffix="+" />
              </span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-medium mt-1 block">
                Års erfaring
              </span>
            </div>
            <div className="p-2">
              <span className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light text-slate-900 block">
                <AnimatedCounter target={13539} suffix="+" />
              </span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-medium mt-1 block">
                Både solgt
              </span>
            </div>
            <div className="p-2">
              <span className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light text-slate-900 block">
                <AnimatedCounter target={100} suffix="%" />
              </span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-medium mt-1 block">
                Tryg handel
              </span>
            </div>
            <div className="p-2">
              <span className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light text-slate-900 block">
                <AnimatedCounter target={24} suffix="/7" />
              </span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-medium mt-1 block">
                Rådgivning
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 3. KONTAKTFORDELE GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Interactive Tel */}
          <a
            href="tel:40207034"
            className="group bg-white p-6 rounded-2xl border border-slate-200 hover:border-slate-900 transition-all flex items-start gap-4 shadow-sm"
          >
            <div className="w-10 h-10 rounded-xl bg-[#1E7BC1] text-white flex items-center justify-center shrink-0 group-hover:bg-[#2BABE3] transition-colors">
              <i className="fa-solid fa-phone text-base" aria-hidden="true" />
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold block">
                Ring direkte
              </span>
              <span className="font-serif font-semibold text-lg text-slate-900 group-hover:text-[#2BABE3] transition-colors whitespace-nowrap block mt-0.5">
                40 20 70 34
              </span>
              <p className="text-xs text-slate-500 font-light mt-1">Vi svarer hele døgnet</p>
            </div>
          </a>

          {/* Interactive Email */}
          <a
            href="mailto:info@nordfyn-marine.dk"
            className="group bg-white p-6 rounded-2xl border border-slate-200 hover:border-slate-900 transition-all flex items-start gap-4 shadow-sm"
          >
            <div className="w-10 h-10 rounded-xl bg-[#1E7BC1] text-white flex items-center justify-center shrink-0 group-hover:bg-[#2BABE3] transition-colors">
              <i className="fa-solid fa-envelope text-base" aria-hidden="true" />
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold block">
                Skriv e-mail
              </span>
              <span className="font-serif font-semibold text-base text-slate-900 group-hover:text-[#2BABE3] transition-colors whitespace-nowrap block mt-0.5">
                info@nordfyn-marine.dk
              </span>
              <p className="text-xs text-slate-500 font-light mt-1">Svarer typisk inden for samme dag</p>
            </div>
          </a>

          {/* Non-interactive Solgt eller gratis Card */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 flex items-start gap-4 shadow-sm md:col-span-2 lg:col-span-1">
            <div className="w-10 h-10 rounded-xl bg-[#1E7BC1] text-white flex items-center justify-center shrink-0">
              <i className="fa-solid fa-handshake-simple text-base" aria-hidden="true" />
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold block">
                Tryghed
              </span>
              <span className="font-serif font-semibold text-lg text-slate-900 block mt-0.5">
                Solgt eller gratis
              </span>
              <p className="text-xs text-slate-500 font-light mt-1">
                Bliver båden ikke solgt, er det gratis
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. BÅDKATEGORIER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6 border-b border-slate-200 pb-6">
          <div>
            <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2BABE3] block mb-1">
              Bådtyper & Modeller
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-light text-slate-900 uppercase tracking-wider">
              Hvilken type båd passer til dig?
            </h2>
          </div>
          <Link
            to="/baade"
            className="inline-flex items-center justify-center gap-3 px-6 py-3 bg-[#071320] hover:bg-[#1E7BC1] text-white transition-colors duration-300 rounded-xl group self-start md:self-auto"
          >
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em]">Se alle bådkategorier</span>
            <i className="fa-solid fa-arrow-right text-xs transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Motorbåde */}
          <Link
            to="/motorbaade"
            className="group relative aspect-[2/3] min-h-[500px] w-full overflow-hidden bg-[#071320] border border-slate-200 hover:border-slate-900 transition-all duration-500 shadow-md hover:shadow-2xl flex flex-col justify-end rounded-2xl"
          >
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/moderne-luksus-motorbaad-kategori.png"
              alt="Motorbåde"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#071320]/95 via-[#071320]/40 to-transparent" />

            {/* Floating Circle Arrow */}
            <div className="absolute top-5 right-5 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#071320] shadow-xl transition-transform duration-300 group-hover:scale-110 group-hover:bg-[#2BABE3] group-hover:text-white">
              <i className="fa-solid fa-arrow-right text-sm" />
            </div>

            {/* Bottom Content Overlay */}
            <div className="relative z-10 p-6 sm:p-8 flex flex-col justify-end gap-2">
              <span className="text-[10px] font-semibold text-[#2BABE3] uppercase tracking-[0.25em]">
                Kabinecruisere & Styrepultsbåde
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl text-white font-light uppercase tracking-wider leading-tight">
                Motorbåde
              </h3>
              <p className="text-xs text-white/80 leading-relaxed font-light mt-1 max-w-xs">
                Fra kabinecruisere til åbne styrepultsbåde. Perfekt til komfortabel tursejlads og fart på vandet.
              </p>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase font-semibold tracking-[0.2em] text-white mt-3 group-hover:text-[#2BABE3] transition-colors">
                <span className="border-b border-white/40 pb-0.5 group-hover:border-[#2BABE3]">Se udvalg</span>
                <i className="fa-solid fa-arrow-right text-[9px]" />
              </div>
            </div>
          </Link>

          {/* Motorsejlere */}
          <Link
            to="/motorsejlere"
            className="group relative aspect-[2/3] min-h-[500px] w-full overflow-hidden bg-[#071320] border border-slate-200 hover:border-slate-900 transition-all duration-500 shadow-md hover:shadow-2xl flex flex-col justify-end rounded-2xl"
          >
            <img
              src="https://assets.auxodev.dk/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/motorsejler.jpg"
              alt="Motorsejlere"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#071320]/95 via-[#071320]/40 to-transparent" />

            {/* Floating Circle Arrow */}
            <div className="absolute top-5 right-5 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#071320] shadow-xl transition-transform duration-300 group-hover:scale-110 group-hover:bg-[#2BABE3] group-hover:text-white">
              <i className="fa-solid fa-arrow-right text-sm" />
            </div>

            {/* Bottom Content Overlay */}
            <div className="relative z-10 p-6 sm:p-8 flex flex-col justify-end gap-2">
              <span className="text-[10px] font-semibold text-[#2BABE3] uppercase tracking-[0.25em]">
                Rummelige Dækshusbåde
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl text-white font-light uppercase tracking-wider leading-tight">
                Motorsejlere
              </h3>
              <p className="text-xs text-white/80 leading-relaxed font-light mt-1 max-w-xs">
                Det bedste fra to verdener: stor rummelighed, lukket dækshus og fremragende sejlegenskaber.
              </p>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase font-semibold tracking-[0.2em] text-white mt-3 group-hover:text-[#2BABE3] transition-colors">
                <span className="border-b border-white/40 pb-0.5 group-hover:border-[#2BABE3]">Se udvalg</span>
                <i className="fa-solid fa-arrow-right text-[9px]" />
              </div>
            </div>
          </Link>

          {/* Sejlbåde */}
          <Link
            to="/sejlbaade"
            className="group relative aspect-[2/3] md:aspect-[16/9] lg:aspect-[2/3] min-h-[500px] md:min-h-[360px] lg:min-h-[500px] w-full overflow-hidden bg-[#071320] border border-slate-200 hover:border-slate-900 transition-all duration-500 shadow-md hover:shadow-2xl flex flex-col justify-end rounded-2xl md:col-span-2 lg:col-span-1"
          >
            <img
              src="https://assets.auxodev.dk/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/sejlbaad.jpg"
              alt="Sejlbåde"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#071320]/95 via-[#071320]/40 to-transparent" />

            {/* Floating Circle Arrow */}
            <div className="absolute top-5 right-5 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#071320] shadow-xl transition-transform duration-300 group-hover:scale-110 group-hover:bg-[#2BABE3] group-hover:text-white">
              <i className="fa-solid fa-arrow-right text-sm" />
            </div>

            {/* Bottom Content Overlay */}
            <div className="relative z-10 p-6 sm:p-8 flex flex-col justify-end gap-2">
              <span className="text-[10px] font-semibold text-[#2BABE3] uppercase tracking-[0.25em]">
                Klassiske & Moderne Turbåde
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl text-white font-light uppercase tracking-wider leading-tight">
                Sejlbåde
              </h3>
              <p className="text-xs text-white/80 leading-relaxed font-light mt-1 max-w-xs">
                Klassiske og moderne turbåde for sejlerentusiaster. Oplev glæden ved sejlads i de danske farvande.
              </p>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase font-semibold tracking-[0.2em] text-white mt-3 group-hover:text-[#2BABE3] transition-colors">
                <span className="border-b border-white/40 pb-0.5 group-hover:border-[#2BABE3]">Se udvalg</span>
                <i className="fa-solid fa-arrow-right text-[9px]" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* 5. UDVALGTE BÅDE (DYNAMIC FROM CMS) */}
      <section className="bg-slate-50 py-16 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
            <div>
              <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2BABE3]">
                Aktuelt udbud
              </span>
              <h2 className="text-3xl sm:text-4xl font-serif font-light text-slate-900 mt-1">
                Udvalgte både til salg
              </h2>
            </div>
            <Link
              to="/baade"
              className="inline-flex items-center gap-2 text-[10px] uppercase font-semibold tracking-[0.2em] text-[#1E7BC1] hover:text-[#2BABE3] transition-colors"
            >
              <span>Se samlet bådeoversigt</span>
              <i className="fa-solid fa-arrow-right text-[9px]" />
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin text-3xl text-[#2BABE3]">
                <i className="fa-solid fa-spinner" />
              </div>
              <p className="text-xs text-slate-500 mt-2 font-light">Henter aktuelle både...</p>
            </div>
          ) : featuredBoats.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {featuredBoats.map((boat, index) => (
                  <div key={boat.id || boat.url_slug} className={index >= 4 ? 'hidden lg:block' : ''}>
                    <BoatCard boat={boat} />
                  </div>
                ))}
              </div>

              <div className="mt-10 text-center">
                <Link
                  to="/baade"
                  className="inline-flex items-center gap-2 bg-[#1E7BC1] hover:bg-[#2BABE3] text-white px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] transition-all rounded-xl shadow-md hover:shadow-lg"
                >
                  <span>Se mere</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </>
          ) : (
            <p className="text-center text-slate-500 py-8 font-light text-xs">Ingen både fundet.</p>
          )}
        </div>
      </section>

      {/* 6. SÆLG DIN BÅD HIGHLIGHT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#071320] text-white relative overflow-hidden border border-slate-800 rounded-3xl shadow-2xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch relative z-10">
            {/* Left Column - Content */}
            <div className="lg:col-span-7 space-y-6 p-8 sm:p-12 lg:p-16">
              <span className="inline-block text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2BABE3] bg-[#2BABE3]/20 px-3 py-1 border border-[#2BABE3]/30 rounded-full">
                Professionel formidling
              </span>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light text-white leading-tight">
                Går du med tanker om at sælge din båd?
              </h2>

              <p className="text-slate-300 text-xs sm:text-sm font-light leading-relaxed">
                Hos Nordfyn Marine tager vi os af alt det praktiske ved dit bådsalg: markedsføring, fremvisning, købsaftale og professionel rådgivning – alt sammen efter vores populære princip{' '}
                <strong className="text-white font-medium">Solgt eller gratis</strong>.
              </p>

              <ul className="list-none space-y-3 text-xs text-slate-300 font-light pt-2">
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-circle-check text-[#2BABE3] text-xs" aria-hidden="true" />
                  <span>Ingen oprettelsesgebyrer eller løbende salgsomkostninger</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-circle-check text-[#2BABE3] text-xs" aria-hidden="true" />
                  <span>Mange års erfaring med bådsalg i hele Danmark</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-circle-check text-[#2BABE3] text-xs" aria-hidden="true" />
                  <span>Sikker og professionel håndtering af slutseddel og betaling</span>
                </li>
              </ul>

              <div className="pt-4 flex flex-wrap gap-4">
                <Link
                  to="/saelg-din-baad"
                  className="inline-flex items-center gap-2 border border-[#2BABE3] bg-[#2BABE3] hover:bg-white hover:text-[#1E7BC1] text-white px-7 py-3.5 text-[11px] uppercase tracking-[0.2em] font-semibold transition-all rounded-xl shadow-md"
                >
                  <span>Sæt din båd til salg her</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Right Column - Featured Image (Full Height) */}
            <div className="lg:col-span-5 relative group min-h-[320px] sm:min-h-[400px] lg:min-h-full">
              <div className="absolute inset-0 overflow-hidden">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/smuk-luksus-motorbaad-hero.png"
                  alt="Sælg din båd hos Nordfyn Marine"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#071320]/80 via-[#071320]/20 to-transparent lg:bg-gradient-to-r lg:from-[#071320] lg:via-transparent lg:to-transparent pointer-events-none" />
              </div>
              {/* Decorative Accent Tag */}
              <div className="absolute bottom-6 right-6 bg-[#1E7BC1] border border-[#2BABE3]/50 text-white text-[10px] uppercase tracking-[0.2em] px-4 py-2 font-semibold shadow-2xl flex items-center gap-2 z-10 rounded-full">
                <i className="fa-solid fa-handshake text-[#2BABE3]" aria-hidden="true" />
                <span>Solgt eller gratis</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. OM NORDFYN MARINE INTRO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-16 sm:my-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2BABE3]">
              Om Nordfyn Marine
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-light text-slate-900 leading-tight">
              Mange års erfaring og stor passion for både
            </h2>
            <p className="text-slate-600 text-xs sm:text-sm font-light leading-relaxed">
              Nordfyn Marine i Otterup drives med fokus på nærvær, faglig stolthed og personlig rådgivning. Hvad enten du skal købe din første motorbåd eller sælge en kærkommen turbåd, er du i trygge hænder hos os.
            </p>
            <div className="pt-2">
              <Link
                to="/om-os"
                className="inline-flex items-center gap-2 border border-[#1E7BC1] bg-[#1E7BC1] hover:bg-[#2BABE3] hover:border-[#2BABE3] text-white px-6 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold transition-all rounded-xl"
              >
                <span>Læs hele historien om os</span>
                <i className="fa-solid fa-arrow-right text-[9px]" />
              </Link>
            </div>
          </div>

          <div className="relative rounded-2xl overflow-hidden border border-slate-200 aspect-[4/3]">
            <img
              src="https://assets.auxodev.dk/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/shutterstock-47683060.jpg"
              alt="Nordfyn Marine"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>
    </div>
  )
}
